import routes from '@/router/routes'
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import store from '@/store'
import { buildLoginRedirectLocation, getPostAuthRedirectTarget } from '@/lib/helpers/deeplink'

// Going back, the page may still be too short for the reader's position when its list arrives
// after it opens. Go as near as the page allows at once, then finish the move as soon as it is tall
// enough: checked before each frame, a second at most, so the list first appears in place.
const restorePosition = (position) =>
    new Promise((resolve) => {
        const giveUpAt = performance.now() + 1000
        const check = () => {
            if (document.documentElement.scrollHeight - window.innerHeight >= position.top) resolve(position)
            else if (performance.now() > giveUpAt) resolve(false)
            else requestAnimationFrame(check)
        }
        window.scrollTo(position)
        check()
    })

const router = createRouter({
    history: createWebHistory(),
    routes,
    // The document scrolls. A new page starts at the top, going back returns to where the
    // reader left that page, and a query-only change (list filters, calendar period and date,
    // pagination) keeps the reader where they are. A reload or a cold start opens at the top.
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition && from !== START_LOCATION) return restorePosition(savedPosition)
        if (to.path !== from.path) return { top: 0 }
        return false
    },
})

router.beforeEach(async (to) => {
    // All pages do have authentication by default.
    // If a page required no authentication, the
    // meta property `guest` can be used to bypass
    // this check.
    if (store.state.auth.initialCheckDone) {
        if (!to.meta?.guest && !store.getters['auth/loggedIn']) {
            // The page requires authentication
            // and the user is not logged in.
            // Must redirect to login page.
            if (to.meta?.redirect === false) {
                //block the redirection
                return true
            }

            return buildLoginRedirectLocation(to)
        } else if (to.meta?.guest && store.getters['auth/loggedIn']) {
            // This page required no authentication
            // and the user is logged in,
            // Must redirect to the intended post-auth target.
            if (to.meta?.redirect === false) {
                //block the redirection
                return true
            }
            return getPostAuthRedirectTarget(to)
        }
    }

    return true
})

export default router
